/**
 * Small helpers for Progressive Web App behavior: detecting whether the app is running as an
 * installed/standalone PWA and managing the offline engine cache. Connectivity detection lives in
 * ./connectivity.js (ping-backed) and is re-exported here so callers have a single PWA import.
 */

// Ping-backed connectivity detection. Re-exported so existing imports from './pwa.js' keep working,
// while the authoritative logic (which works around navigator.onLine lying on cached loads) lives in
// connectivity.ts.
export { isOffline, isOnline, onConnectivityChange, checkConnectivityNow, navigationLoadedOnline } from './connectivity.js';

/** True when running as an installed PWA (standalone display mode, or an iOS home-screen app). */
export function isInstalledPwa(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches ?? false;
  // iOS Safari exposes navigator.standalone for home-screen ("Add to Home Screen") apps.
  const iosStandalone = (navigator as unknown as { standalone?: boolean }).standalone === true;
  return standalone || iosStandalone;
}

/** Marker the Service Worker writes into its (version-named) precache once the offline engine is cached. */
const OFFLINE_ENGINE_READY_URL = '/__offline-engine-ready';

/**
 * True if the offline engine for the CURRENT app version is cached and ready to play. The Service
 * Worker writes a marker into its precache after the whole engine tier is cached; because old precaches
 * are deleted when a new app version activates, the marker's presence means the engine is cached for
 * the version now in use. A non-installed client that hasn't re-cached after an update reports false,
 * so the UI can show an honest message instead of offering a broken offline game.
 */
export async function isOfflinePlayReady(): Promise<boolean> {
  if (typeof caches === 'undefined') {
    return false;
  }
  try {
    return (await caches.match(OFFLINE_ENGINE_READY_URL)) != null;
  } catch {
    return false;
  }
}

/**
 * Asks the active Service Worker to cache the offline engine tier (and mark it ready) for the current
 * version. Best-effort and idempotent. Called eagerly for installed PWAs (so offline play is always
 * available and stays current across updates) and once when a normal browser tab opens /offline.
 */
export function requestOfflineEngineCaching(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }
  navigator.serviceWorker.ready
    .then((registration) => registration.active?.postMessage({ type: 'cache-offline-engine' }))
    .catch(() => {
      // Best-effort; the engine is also cached opportunistically as the /offline page loads.
    });
}
